import { selectViewportPageIndexInStore, setViewportPageIndexInStore, useAppDispatch, useAppSelector } from '@/store';
import { useCallback, useEffect, useRef } from 'react';

interface BundleSectionsTreeRef {
  setBundleSectionsTreeNodeIdByViewportPageIndex: (viewportPageIndex: number) => void;
}
interface GroupedVirtuosoThumbnailsListRef {
  scrollIntoViewByViewportPageIndex: (
    viewportPageIndex: number,
    groupedVirtuosoThumbnailsListAlign: 'center' | 'end' | 'start',
  ) => void;
}

interface ReactVirtualizedRef {
  scrollToRowWithDebounceByViewportPageIndex: (viewportPageIndex: number) => void;
  openChronologyForCurrentPage: () => void;
}

export interface GroupedTanStackVirtualThumbnailsListRef {
  scrollToItemByPageIndex: (pageIndex: number) => void;
  scrollToItemByPageId: (pageId: string) => void;
}

interface ViewportPageIndexOptions {
  groupedVirtuosoThumbnailsListAlign?: 'center' | 'end' | 'start';
  skipCallBundleSectionTree?: boolean;
}
export const useViewportPageIndex = () => {
  const reactVirtualizedRef = useRef<ReactVirtualizedRef>(null);
  const pageNumberInputRef = useRef<HTMLInputElement | null>(null);
  const bundleSectionsTreeRef = useRef<BundleSectionsTreeRef>(null);
  const groupedVirtuosoThumbnailsListRef = useRef<GroupedVirtuosoThumbnailsListRef>(null);
  const groupedTanStackVirtualThumbnailsListRef = useRef<GroupedTanStackVirtualThumbnailsListRef>(null);

  const dispatch = useAppDispatch();
  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);
  // Held in a ref rather than state because it's only ever read inside the
  // effect below — putting it in state caused a wasted re-render of every
  // consumer on every viewport change (since the effect always reset it to {}).
  const viewportPageIndexOptionsRef = useRef<ViewportPageIndexOptions>({});

  /**
   * This function is used exclusively for setting the value from the List component (react-virtualized). Do not use it anywhere else.
   * If you need to set new page index use onScrollToPageIndexIneReactVirtualizedViewport.
   */
  const onChangeReactVirtualizedViewportPageIndex = useCallback((newReactVirtualizedViewportPageIndex: number) => {
    dispatch(setViewportPageIndexInStore(newReactVirtualizedViewportPageIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScrollToPageIndexInReactVirtualizedViewport = useCallback(
    (newReactVirtualizedViewportPageIndex: number, newViewportPageIndexOptions?: ViewportPageIndexOptions) => {
      if (reactVirtualizedRef && reactVirtualizedRef.current && newReactVirtualizedViewportPageIndex >= 0) {
        reactVirtualizedRef.current.scrollToRowWithDebounceByViewportPageIndex(newReactVirtualizedViewportPageIndex);

        if (newViewportPageIndexOptions) {
          viewportPageIndexOptionsRef.current = newViewportPageIndexOptions;
        }
      }
    },
    [],
  );

  useEffect(() => {
    const viewportPageIndexOptions = viewportPageIndexOptionsRef.current;

    if (pageNumberInputRef.current) {
      pageNumberInputRef.current.value = `${viewportPageIndexInStore + 1}`;
    }
    if (bundleSectionsTreeRef.current && !viewportPageIndexOptions.skipCallBundleSectionTree) {
      bundleSectionsTreeRef.current.setBundleSectionsTreeNodeIdByViewportPageIndex(viewportPageIndexInStore);
    }
    if (groupedVirtuosoThumbnailsListRef.current) {
      groupedVirtuosoThumbnailsListRef.current.scrollIntoViewByViewportPageIndex(
        viewportPageIndexInStore,
        viewportPageIndexOptions.groupedVirtuosoThumbnailsListAlign || 'center',
      );
    }
    if (groupedTanStackVirtualThumbnailsListRef.current) {
      groupedTanStackVirtualThumbnailsListRef.current.scrollToItemByPageIndex(viewportPageIndexInStore);
    }

    viewportPageIndexOptionsRef.current = {};
  }, [viewportPageIndexInStore]);

  useEffect(() => {
    if (
      pageNumberInputRef.current &&
      pageNumberInputRef.current.dataset &&
      pageNumberInputRef.current.dataset.maxDigits
    ) {
      const numberInput = pageNumberInputRef.current;
      const maxDigits = parseInt(numberInput.dataset.maxDigits || '1', 10);

      const handleInput = () => {
        if (numberInput.value.length > maxDigits) {
          numberInput.value = numberInput.value.slice(0, maxDigits);
        }
      };

      numberInput.addEventListener('input', handleInput);

      return () => {
        numberInput.removeEventListener('input', handleInput);
      };
    }
  }, [pageNumberInputRef.current?.dataset.maxDigits]);

  return {
    reactVirtualizedRef,
    pageNumberInputRef,
    bundleSectionsTreeRef,
    groupedVirtuosoThumbnailsListRef,
    groupedTanStackVirtualThumbnailsListRef,
    onChangeReactVirtualizedViewportPageIndex,
    onScrollToPageIndexInReactVirtualizedViewport,
  };
};
